/**
 * Delivered-metric formatting, shared by every surface that shows a campaign's
 * platform numbers — the Campaign Grid rows and the Campaign Launch Workflow's
 * "delivered to date" strip. Shared deliberately: the two render the SAME
 * cached figures, and a campaign whose spend reads ₹4,085,541.05 in the grid
 * and $4,085,541.05 on the page it links to looks like two different campaigns.
 */

/**
 * Money in the campaign's platform currency. `code` is an ISO 4217 code (from
 * the Google Ads customer via the grid pull); omitted or unknown falls back to
 * the legacy plain-$ rendering, so USD output is unchanged.
 */
export const cur = (v, code) => {
  if (v == null) return '—';
  if (code && code !== 'USD') {
    try {
      return Number(v).toLocaleString('en-US', {
        style: 'currency', currency: code,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    } catch (e) { /* unknown code — fall through to legacy $ */ }
  }
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** A whole count (impressions, clicks). */
export const num = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  // Never print "NaN" in a reviewed report — an unparseable value reads as
  // missing, which it effectively is.
  return Number.isFinite(n) ? Math.round(n).toLocaleString() : '—';
};

// Conversions come off the platform as FLOATS (Google Ads reports e.g. 3.5), so
// they must not be rounded to an integer the way impressions/clicks are — 0.4
// conversions is not "0". Integers still render clean (no trailing ".0").
export const numFlex = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/** A KPI rate stored as a fraction (0.015 → '1.50%'). */
export const rate = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : '—';
};
